import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/solid";
import { Badge, Box, Checkbox, Group, Loader, Table, Text, UnstyledButton, useMantineColorScheme } from "@mantine/core";
import { SVGProps } from "react";
import { ScoreGauge } from "../../../components/common/ScoreGauge";
import { useTranslation } from "../../../hooks/useTranslation";
import { SortDirection } from "../../../models/person.model";
import { formatShortDate } from "../../../utils/dateFormat";
import { toTitleCase } from "../../../utils/textFormat";
import { EmployeeOverviewListItem } from "../models/employeeOverview.model";
import { AllocationBadgeGroup } from "./AllocationBadgeGroup";

const IconChevronUp = (props: SVGProps<SVGSVGElement>) => <ChevronUpIcon {...props} />;
const IconChevronDown = (props: SVGProps<SVGSVGElement>) => <ChevronDownIcon {...props} />;

interface EmployeeOverviewTableProps {
    employees: EmployeeOverviewListItem[];
    loading: boolean;
    sortField: string | null;
    sortDirection: SortDirection;
    onSortChange: (field: string) => void;
    onSelectEmployee: (employee: EmployeeOverviewListItem) => void;
    selectionMode?: boolean;
    selectedIds?: Set<number>;
    onToggleSelect?: (employee: EmployeeOverviewListItem) => void;
}

interface SortableHeaderProps {
    field: string;
    label: string;
    sortField: string | null;
    sortDirection: SortDirection;
    onSortChange: (field: string) => void;
}

function SortableHeader({ field, label, sortField, sortDirection, onSortChange }: SortableHeaderProps) {
    const { t } = useTranslation();
    const isActive = sortField === field;
    return (
        <UnstyledButton onClick={() => onSortChange(field)} aria-label={t("EMPLOYEEOVERVIEW_SORT_BY_ARIA", { label }, `Sort by ${label}`)}>
            <Group gap={4} wrap="nowrap">
                <Text fw={600} size="sm">
                    {label}
                </Text>
                {isActive &&
                    (sortDirection === SortDirection.Ascending ? (
                        <IconChevronUp style={{ width: 12, height: 12 }} />
                    ) : (
                        <IconChevronDown style={{ width: 12, height: 12 }} />
                    ))}
            </Group>
        </UnstyledButton>
    );
}

export function EmployeeOverviewTable({
    employees,
    loading,
    sortField,
    sortDirection,
    onSortChange,
    onSelectEmployee,
    selectionMode = false,
    selectedIds,
    onToggleSelect,
}: EmployeeOverviewTableProps) {
    const { colorScheme } = useMantineColorScheme();
    const { t } = useTranslation();
    if (loading) {
        return (
            <Box p="xl" style={{ textAlign: "center" }}>
                <Loader size="md" color="brand" />
                <Text mt="md">{t("EMPLOYEEOVERVIEW_LOADING", undefined, "Loading employees...")}</Text>
            </Box>
        );
    }

    if (employees.length === 0) {
        return (
            <Box p="xl" style={{ textAlign: "center" }}>
                <Text size="lg" fw={500} c="dimmed">
                    {t("EMPLOYEEOVERVIEW_NO_RESULTS", undefined, "No employees found matching your search criteria")}
                </Text>
            </Box>
        );
    }

    const headerProps = { sortField, sortDirection, onSortChange };

    return (
        <Table.ScrollContainer minWidth={1000}>
            <Table verticalSpacing="sm" striped highlightOnHover>
                <Table.Thead>
                    <Table.Tr>
                        {selectionMode && <Table.Th w={44} />}
                        <Table.Th>
                            <SortableHeader field="firstName" label={t("EMPLOYEEOVERVIEW_COL_NAME", undefined, "Name")} {...headerProps} />
                        </Table.Th>
                        <Table.Th>{t("EMPLOYEEOVERVIEW_COL_EMAIL", undefined, "Email")}</Table.Th>
                        <Table.Th>{t("EMPLOYEEOVERVIEW_COL_ALLOCATIONS", undefined, "Allocations")}</Table.Th>
                        <Table.Th>{t("EMPLOYEEOVERVIEW_COL_POSITION", undefined, "Position")}</Table.Th>
                        <Table.Th>{t("EMPLOYEEOVERVIEW_COL_EMPLOYMENT_TYPE", undefined, "Employment type")}</Table.Th>
                        <Table.Th>
                            <SortableHeader field="workStartDate" label={t("EMPLOYEEOVERVIEW_COL_WORK_START", undefined, "Work start")} {...headerProps} />
                        </Table.Th>
                        <Table.Th>
                            <SortableHeader field="workEndDate" label={t("EMPLOYEEOVERVIEW_COL_WORK_END", undefined, "Work end")} {...headerProps} />
                        </Table.Th>
                        <Table.Th>
                            <SortableHeader field="availabilityDate" label={t("EMPLOYEEOVERVIEW_AVAILABILITY_LABEL", undefined, "Availability")} {...headerProps} />
                        </Table.Th>
                        <Table.Th>{t("EMPLOYEEOVERVIEW_COL_PROFILE", undefined, "Profile")}</Table.Th>
                    </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                    {employees.map((employee) => {
                        const isSelected = selectedIds?.has(employee.employeeId) ?? false;
                        return (
                        <Table.Tr
                            key={employee.employeeId}
                            onClick={() => {
                                if (selectionMode) {
                                    onToggleSelect?.(employee);
                                    return;
                                }
                                onSelectEmployee(employee);
                            }}
                            style={{
                                cursor: "pointer",
                                background: selectionMode && isSelected
                                    ? "light-dark(var(--mantine-color-materialAmber-0), rgba(255, 193, 7, 0.14))"
                                    : undefined,
                            }}
                        >
                            {selectionMode && (
                                <Table.Td onClick={(e) => e.stopPropagation()}>
                                    <Checkbox
                                        checked={isSelected}
                                        onChange={() => onToggleSelect?.(employee)}
                                        aria-label={t(
                                            "EMPLOYEEOVERVIEW_SELECT_ARIA",
                                            { name: `${employee.firstName} ${employee.lastName}` },
                                            `Select ${employee.firstName} ${employee.lastName}`
                                        )}
                                    />
                                </Table.Td>
                            )}
                            <Table.Td>
                                <Text fw={500}>
                                    {toTitleCase(employee.firstName)} {toTitleCase(employee.lastName)}
                                </Text>
                                {!employee.isActive && (
                                    <Badge color="gray.7" variant="filled" size="xs" radius="sm" mt={2} style={{ textTransform: "none" }}>
                                        {t("EMPLOYEEOVERVIEW_INACTIVE_BADGE", undefined, "Inactive")}
                                    </Badge>
                                )}
                            </Table.Td>
                            <Table.Td>{employee.email}</Table.Td>
                            <Table.Td>
                                <AllocationBadgeGroup allocations={employee.allocations} />
                            </Table.Td>
                            <Table.Td>
                                {employee.positionName ? (
                                    <Badge color={colorScheme === "dark" ? "brand.1" : "brand"} variant="outline"
                                        radius="sm" style={{ textTransform: "none" }}>
                                        {employee.positionName}
                                    </Badge>
                                ) : (
                                    <Badge color="gray" variant="outline" radius="sm">
                                        {t("EMPLOYEEOVERVIEW_NO_POSITION_BADGE", undefined, "No position")}
                                    </Badge>
                                )}
                            </Table.Td>
                            <Table.Td>{employee.contractTypeName ?? "—"}</Table.Td>
                            <Table.Td>{formatShortDate(employee.workStartDate)}</Table.Td>
                            <Table.Td>{formatShortDate(employee.workEndDate)}</Table.Td>
                            <Table.Td>{formatShortDate(employee.availabilityDate)}</Table.Td>
                            <Table.Td>
                                {employee.personId && <ScoreGauge score={employee.profileCompletionScore} size={32} />}
                            </Table.Td>
                        </Table.Tr>
                        );
                    })}
                </Table.Tbody>
            </Table>
        </Table.ScrollContainer>
    );
}
